import { observer } from 'mobx-react-lite'
import { useSteleBreakpoint } from '@/src/hooks/useBreakpoint'
import { useId } from 'react'
import clsx from 'clsx'
import IconChamele from '../icons/IconChamele'

type LevelProgressBarProps = {
    progress: number
    label?: string
    className?: string
    theme?: 'orange' | 'red'
    showChameleIndicator?: boolean
}

const TRACK_PATH = 'M6.47199 17.9988C63.8196 3.11596 124.001 3.0665 181.373 17.8551'
const FILL_PATH = 'M6.81516 17.91C63.9134 3.15364 123.809 3.09607 180.935 17.7426'
const SHINE_PATH = 'M7.66917 16.4903C64.1319 2.05021 123.301 1.96907 179.803 16.2543'

function clampProgress(progress: number) {
    return Math.min(Math.max(progress, 0), 1)
}

const LevelProgressBar = observer(function LevelProgressBar({
    progress,
    label,
    className,
    theme = 'orange',
    showChameleIndicator = false,
}: LevelProgressBarProps) {

    const isStele = useSteleBreakpoint();

    const viewBoxWidth = 188
    const viewBoxHeight = 25
    const renderWidth = isStele ? 1062 : 180
    const renderHeight = isStele ? 150 : 24

    const baseId = useId().replace(/:/g, '')
    const fillGradientId = `${baseId}-level-bar-fill-gradient`
    const shineGradientId = `${baseId}-level-bar-shine-gradient`
    const progressClipId = `${baseId}-level-bar-progress-clip`
    const clampedProgress = clampProgress(progress)
    const progressWidth = viewBoxWidth * clampedProgress
    const indicatorLeft = `${clampedProgress * 100}%`
    const isRedTheme = theme === 'red'
    const baseTrackStroke = '#8A0B00'
    const innerTrackStroke = isRedTheme ? '#BB2A24' : '#A54A20'
    const fillStart = isRedTheme ? '#E96178' : '#FFB001'
    const fillEnd = isRedTheme ? '#DD0B2F' : '#FF7300'

    return (
        <div className='relative'>
            <svg
                width={renderWidth}
                height={renderHeight}
                viewBox={`0 0 ${viewBoxWidth} ${viewBoxHeight}`}
                fill='none'
                xmlns='http://www.w3.org/2000/svg'
                className={className}
                role='meter'
                aria-label='Level progress'
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={Math.round(clampedProgress * 100)}
            >
                <path
                    d={TRACK_PATH}
                    stroke={baseTrackStroke}
                    strokeWidth='12'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                />
                <path
                    d={TRACK_PATH}
                    stroke={innerTrackStroke}
                    strokeWidth='8'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                />
                <g clipPath={`url(#${progressClipId})`}>
                    <path
                        d={TRACK_PATH}
                        stroke={`url(#${fillGradientId})`}
                        strokeWidth='8'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                    />
                    <path
                        opacity='0.35'
                        d={FILL_PATH}
                        stroke={`url(#${fillGradientId})`}
                        strokeWidth='8'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                    />
                </g>
                <path
                    d={SHINE_PATH}
                    stroke={`url(#${shineGradientId})`}
                    strokeWidth='4'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                />
                <defs>
                    <clipPath id={progressClipId}>
                        <rect
                            x='-12'
                            y='0'
                            width={progressWidth + 12}
                            height={viewBoxHeight}
                            rx='14'
                            style={{ transition: 'width 150ms linear' }}
                        />
                    </clipPath>
                    <radialGradient id={fillGradientId} cx='0' cy='0' r='1' gradientUnits='userSpaceOnUse' gradientTransform='translate(94.8004 707.6) rotate(-90) scale(707.4 523.453)'>
                        <stop offset='0.985617' stopColor={fillStart} />
                        <stop offset='0.996184' stopColor={fillEnd} />
                    </radialGradient>
                    <radialGradient id={shineGradientId} cx='0' cy='0' r='1' gradientUnits='userSpaceOnUse' gradientTransform='translate(94.8004 706.4) rotate(-90) scale(707.4 523.453)'>
                        <stop offset='0.989297' stopColor='white' stopOpacity='0' />
                        <stop offset='0.996184' stopColor='white' />
                    </radialGradient>
                </defs>
            </svg>
            {showChameleIndicator && (
                <div
                    className={clsx(
                        'top-1/2 z-10 absolute transition-[left] -translate-x-1/2 -translate-y-[58%] duration-150 ease-linear',
                        'w-7 h-7',
                        'stele:w-28 stele:h-28'
                    )}
                    style={{ left: indicatorLeft }}
                >
                    <IconChamele
                        width='100%'
                        height='100%'
                        stroke={baseTrackStroke}
                        stopColor={isRedTheme ? '#FFD6D1' : '#FFD080'}
                    />
                </div>
            )}
        </div>
    )
})

export default LevelProgressBar
